<template>
  <div>
    <div class="absolute-elements">
      <NavBar :sections="sections" @scroll-to-section="scrollToSection" />
    </div>
    <div class="h-screen w-screen overflow-y-auto bg-base-100" @scroll="handleScroll">
      <WaveAnimation :visible="showWave" />
      
      <!-- Hero Section -->
      <section class="section relative overflow-hidden" id="slide1">
        <div class="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"></div>
        <div class="absolute inset-0 bg-pattern opacity-10"></div>
        
        <!-- Decorative Elements -->
        <div class="absolute top-20 left-10 w-32 h-32 bg-primary/20 rounded-full blur-xl animate-float"></div>
        <div class="absolute bottom-20 right-20 w-48 h-48 bg-secondary/10 rounded-full blur-2xl animate-float-slow"></div>
        <div class="absolute top-1/2 left-1/4 w-24 h-24 bg-primary/15 rounded-full blur-lg animate-float-delayed"></div>
        
        <div class="relative z-10 flex items-center justify-center min-h-screen px-6">
          <div class="text-center max-w-5xl mx-auto">
            <!-- Company Badge -->
            <div class="company-badge mb-6">
              <div class="badge-content">
                <span class="badge-text">Manufacturing Excellence Since 1966</span>
              </div>
            </div>
            
            <h1 class="hero-title text-5xl md:text-7xl font-bold text-white mb-6">
              Build Your <span class="text-primary">Career</span> with Us
            </h1>
            <p class="hero-subtitle text-xl md:text-2xl text-white/80 mb-8 leading-relaxed">
              Join STE Tank & Equipment - Where Precision Manufacturing Meets Career Excellence
            </p>
            <p class="hero-description text-lg md:text-xl text-white/70 mb-12 max-w-4xl mx-auto">
              Become part of a team that's revolutionizing stainless steel tank trailer manufacturing. 
              We're looking for skilled professionals who share our commitment to quality, innovation, and excellence.
            </p>
            
            <!-- Hero Stats -->
            <div class="hero-stats grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div class="stat-item">
                <div class="stat-icon">
                  <font-awesome-icon :icon="['fas', 'award']" />
                </div>
                <div class="stat-number">50+</div>
                <div class="stat-label">Years of Excellence</div>
              </div>
              <div class="stat-item">
                <div class="stat-icon">
                  <font-awesome-icon :icon="['fas', 'users']" />
                </div>
                <div class="stat-number">100+</div>
                <div class="stat-label">Team Members</div>
              </div>
              <div class="stat-item">
                <div class="stat-icon">
                  <font-awesome-icon :icon="['fas', 'industry']" />
                </div>
                <div class="stat-number">Infinite</div>
                <div class="stat-label">Opportunities</div>
              </div>
            </div>
            
            <!-- CTA Buttons -->
            <div class="hero-cta flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button 
                @click="scrollToSection('slide2')"
                class="cta-primary inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-primary/90 
                       text-white font-semibold rounded-full transition-all duration-300 
                       hover:shadow-lg hover:shadow-primary/30 hover:scale-105"
              >
                <span>Apply Now</span>
                <font-awesome-icon :icon="['fas', 'arrow-right']" class="animate-pulse" />
              </button>
              
              <button 
                @click="scrollToSection('slide2')"
                class="cta-secondary inline-flex items-center gap-3 px-8 py-4 
                       border-2 border-white/30 hover:border-white/60 text-white 
                       font-semibold rounded-full transition-all duration-300 
                       hover:bg-white/10 hover:scale-105"
              >
                <span>Learn More</span>
                <font-awesome-icon :icon="['fas', 'chevron-down']" class="animate-bounce" />
              </button>
              
              <button 
                @click="scrollToSection('slide3')"
                class="cta-secondary inline-flex items-center gap-3 px-8 py-4 
                       border-2 border-white/30 hover:border-white/60 text-white 
                       font-semibold rounded-full transition-all duration-300 
                       hover:bg-white/10 hover:scale-105"
              >
                <span>Open Positions</span>
                <font-awesome-icon :icon="['fas', 'chevron-down']" class="animate-bounce" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Apply Now Section -->
      <section class="section min-h-screen" id="slide2">
        <div class="section-content">
          <ApplyNow />
        </div>
      </section>

      <!-- Job Openings Section -->
      <section class="section min-h-screen" id="slide3">
        <div class="section-content">
          <JobListings />
        </div>
      </section>

      <!-- Future sections can be added here -->
      <!-- 
      <section class="section" id="slide4">
        <div class="section-content">
          <CompanyCulture />
        </div>
      </section>
      
      <section class="section" id="slide5">
        <div class="section-content">
          <BenefitsOverview />
        </div>
      </section>
      -->
    </div>

    <!-- Scroll Navigation -->
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollUp"
        :icon="isUpHovered ? 'angles-up' : 'chevron-up'"
        class="scroll-arrow up-arrow fixed bottom-[60px] left-[51.5%] transform -translate-x-1/2 text-4xl text-secondary cursor-pointer z-50 hover:scale-125 transition-transform"
        @click="scrollToPreviousSection"
        @mouseenter="isUpHovered = true"
        @mouseleave="isUpHovered = false"
      />
    </transition>
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollDown"
        :icon="isDownHovered ? 'angles-down' : 'chevron-down'"
        class="scroll-arrow down-arrow fixed bottom-[10px] left-[51.5%] transform -translate-x-1/2 text-4xl text-secondary cursor-pointer z-50 hover:scale-125 transition-transform"
        @click="scrollToNextSection"
        @mouseenter="isDownHovered = true"
        @mouseleave="isDownHovered = false"
      />
    </transition>
  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue';
import ApplyNow from '../components/ApplyNow.vue';
import JobListings from '../components/JobListings.vue';
import WaveAnimation from '../components/WaveAnimation.vue';

export default {
  name: 'CareersPage',
  components: {
    NavBar,
    ApplyNow,
    JobListings,
    WaveAnimation
  },
  data() {
    return {
      sections: [
        { name: 'APPLY NOW', id: 'slide2' },
        { name: 'JOB OPENINGS', id: 'slide3' }
        // Future sections can be added here:
        // { name: 'COMPANY CULTURE', id: 'slide4' },
        // { name: 'BENEFITS', id: 'slide5' },
        // { name: 'LOCATIONS', id: 'slide6' }
      ],
      showScrollUp: false,
      showScrollDown: true,
      isUpHovered: false,
      isDownHovered: false,
      showWave: false
    };
  },
  mounted() {
    // Initialize scroll state
    const container = document.querySelector('.overflow-y-auto');
    if (container) {
      this.showScrollUp = container.scrollTop > 100;
      this.showScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight - 100;
      this.showWave = container.scrollTop > container.clientHeight / 2;
    }
  },
  methods: {
    handleScroll(event) {
      const container = event.target;
      this.showScrollUp = container.scrollTop > 100;
      this.showScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight - 100;
      this.showWave = container.scrollTop > container.clientHeight / 2;
    },
    
    scrollToSection(id) {
      console.log(`Scrolling to section: ${id}`);
      const container = document.querySelector('.overflow-y-auto');
      const element = document.getElementById(id);
      if (element) {
        container.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
      }
    },
    
    scrollToNextSection() {
      const container = document.querySelector('.overflow-y-auto');
      const sections = Array.from(document.querySelectorAll('section'));
      const currentScroll = container.scrollTop;
      const tolerance = 5;

      const nextSection = sections.find(
        (section) => section.offsetTop > currentScroll + tolerance
      );

      if (nextSection) {
        console.log(`Scrolling to next section: ${nextSection.id}`);
        container.scrollTo({ top: nextSection.offsetTop, behavior: 'smooth' });
      } else {
        console.log('No next section found');
      }
    },
    
    scrollToPreviousSection() {
      const container = document.querySelector('.overflow-y-auto');
      const sections = Array.from(document.querySelectorAll('section'));
      const currentScroll = container.scrollTop;
      const tolerance = 5;

      let previousSection = null;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.offsetTop + tolerance < currentScroll) {
          previousSection = section;
          break;
        }
      }

      if (previousSection) {
        console.log(`Scrolling to previous section: ${previousSection.id}`);
        container.scrollTo({ top: previousSection.offsetTop, behavior: 'smooth' });
      } else {
        this.scrollToTop();
      }
    },
    
    scrollToTop() {
      const container = document.querySelector('.overflow-y-auto');
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
};
</script>

<style scoped>
.absolute-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  z-index: 100;
}

.h-screen.overflow-y-auto {
  background: radial-gradient(ellipse at bottom, var(--color-base-100) 0%, var(--color-base-300) 90%);
  background-attachment: fixed;
  position: relative;
  scrollbar-width: none;
  z-index: 0;
  -webkit-overflow-scrolling: touch;
}

.section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  width: 100%;
  overflow: hidden;
}

.section-content {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section Styles */
.bg-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

/* Company Badge */
.company-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
}

.badge-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.badge-content:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.company-logo {
  height: 30px;
  width: auto;
  filter: brightness(1.2);
}

.badge-text {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* Hero Title */
.hero-title {
  background: linear-gradient(135deg, #ffffff 0%, #b7b9ba 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  line-height: 1.1;
}

.hero-subtitle {
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

.hero-description {
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

/* Hero Stats */
.hero-stats {
  margin-bottom: 3rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.stat-item:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.stat-icon {
  width: 50px;
  height: 50px;
  background: rgba(143, 0, 0, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8f0000;
  font-size: 1.5rem;
  transition: all 0.3s ease;
}

.stat-item:hover .stat-icon {
  background: rgba(143, 0, 0, 0.4);
  color: #ffffff;
  transform: scale(1.1);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #8f0000;
  line-height: 1;
}

.stat-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 1px;
  text-align: center;
}

/* CTA Buttons */
.hero-cta {
  margin-bottom: 2rem;
}

.cta-primary, .cta-secondary {
  min-width: 200px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cta-primary:hover {
  text-decoration: none;
  color: white;
}

.cta-secondary:hover {
  text-decoration: none;
  color: white;
}

/* Scroll Indicator */
.scroll-indicator {
  animation: pulse-soft 2s infinite;
}

.scroll-arrow-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.scroll-arrow-container:hover {
  transform: translateY(-3px);
}

.scroll-arrow-icon {
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.7);
  transition: color 0.3s ease;
}

.scroll-arrow-container:hover .scroll-arrow-icon {
  color: #8f0000;
}

.scroll-text {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: color 0.3s ease;
}

.scroll-arrow-container:hover .scroll-text {
  color: rgba(255, 255, 255, 0.9);
}

/* Animations */
@keyframes float {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
  }
  50% { 
    transform: translateY(-20px) rotate(2deg); 
  }
}

@keyframes float-slow {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
  }
  50% { 
    transform: translateY(-30px) rotate(-3deg); 
  }
}

@keyframes float-delayed {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
  }
  50% { 
    transform: translateY(-15px) rotate(1deg); 
  }
}

@keyframes pulse-soft {
  0%, 100% {
    opacity: 1;
    transform: translateY(0);
  }
  50% {
    opacity: 0.7;
    transform: translateY(5px);
  }
}

.animate-float {
  animation: float 8s ease-in-out infinite;
}

.animate-float-slow {
  animation: float-slow 12s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float-delayed 10s ease-in-out infinite;
  animation-delay: 2s;
}

/* Fade Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-subtitle {
    font-size: 1.25rem;
  }
  
  .hero-description {
    font-size: 1rem;
  }
  
  .hero-stats {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .stat-number {
    font-size: 2rem;
  }
  
  .hero-cta {
    flex-direction: column;
    gap: 1rem;
  }
  
  .cta-primary, .cta-secondary {
    width: 100%;
    max-width: 300px;
  }
  
  .badge-content {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
  
  .badge-text {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .stat-item {
    padding: 1.5rem 0.75rem;
  }
  
  .company-logo {
    height: 25px;
  }
}
</style>
